import React from 'react';
import { AwardIcon, BrainIcon, CheckCircleIcon, DollarSignIcon, GraduationCapIcon, LaptopIcon, LockIcon } from 'lucide-react';
import { TrackingNoticeLinks } from './TrackingNoticeLinks';
import { ExploreBridge } from '../data/exploreBridges';
import { UniversityData } from '../data/universityData';
const featureIconMap: Record<string, any> = {
  BrainIcon,
  AwardIcon,
  LaptopIcon,
  DollarSignIcon,
  GraduationCapIcon
};
interface BrandedExploreBridgeProps {
  bridge: ExploreBridge;
  university: UniversityData;
  onCta: () => void;
}
// Branded layout for /explore/{brand}-{version} ad copy. Mirrors the
// advertiser page (logo bar, brand-colored hero, stats, features, CTA band)
// but swaps the multi-step form for a single call to action that links out.
export function BrandedExploreBridge({
  bridge,
  university,
  onCta
}: BrandedExploreBridgeProps) {
  const landing = university.landingContent;
  return <div className="min-h-screen font-sans flex flex-col bg-gray-50" data-id="element-127" data-mp="2lxwmokmcl1lc">
      {/* School logo bar */}
      <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-128" data-mp="qhwgyrom1z56">
        <div className="max-w-7xl mx-auto flex items-center justify-center relative" data-id="element-129" data-mp="vgfy8es9emr7">
          {university.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-130" data-mp="10hdh6tl3fktr" /> : <span className="text-xl font-bold" style={{
          color: university.primaryColor
        }} data-id="element-131" data-mp="7ptswv9n3gkw">
              {university.name}
            </span>}
          <div className="hidden md:flex items-center gap-1.5 text-gray-400 text-xs font-medium absolute right-0" data-id="element-132" data-mp="grbj56grgynb">
            <LockIcon className="w-3.5 h-3.5" data-id="element-133" data-mp="ll60424zezip" />
            Secure
          </div>
        </div>
      </header>

      <main className="flex-grow bg-white" data-id="element-134" data-mp="3p8en7gkbjzwr">
        {/* Hero — brand color band with the CTA card in place of the form */}
        <section className="relative py-12 md:py-20 px-4 bg-cover bg-center" style={{
        backgroundColor: university.primaryColor,
        backgroundImage: university.heroBackgroundImage ? `url(${university.heroBackgroundImage})` : undefined
      }} data-id="element-135" data-mp="wdejt91dtiru">
          <div className="relative max-w-6xl mx-auto lg:hidden text-white mb-6" data-id="element-136" data-mp="115zba3wtfjs7">
            <p className="text-sm font-semibold mb-3 text-white/90" data-id="element-137" data-mp="dg4w3fp0xj4s">
              {university.name}
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-tight" data-id="element-138" data-mp="8hacibj8fqjm">
              {landing?.headline || university.tagline}
            </h1>
          </div>

          <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start" data-id="element-139" data-mp="id3nhqhazbza">
            <div className="text-white lg:pt-8 order-2 lg:order-1" data-id="element-140" data-mp="1xtc8dnmyfina">
              <p className="hidden lg:block text-base font-semibold mb-4 text-white/90" data-id="element-141" data-mp="1tu3fekzqnoss">
                {university.name}
              </p>
              <h1 className="hidden lg:block text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6" data-id="element-142" data-mp="21bjfssa5sr6o">
                {landing?.headline || university.tagline}
              </h1>
              {(landing?.subheadline || university.mission) && <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-lg" data-id="element-143" data-mp="1wet56kh4insn">
                  {landing?.subheadline || university.mission}
                </p>}
              {landing?.heroHighlights && landing.heroHighlights.length > 0 && <ul className="mt-6 space-y-2.5 max-w-lg" data-id="element-144" data-mp="2ae2hh927kxrc">
                  {landing.heroHighlights.map(highlight => <li key={highlight} className="flex items-start gap-2.5 text-sm md:text-base text-white/90" data-id="element-145" data-mp="2fbkkkitjoirf">
                      <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-white/70" data-id="element-146" data-mp="37tgagoiuw22r" />
                      <span data-id="element-147" data-mp="1k54rhznbb1cs">{highlight}</span>
                    </li>)}
                </ul>}
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-8 lg:p-10 order-1 lg:order-2 text-center" data-id="element-148" data-mp="1f3xlo64qcc8r">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight" data-id="element-149" data-mp="157cebe58mp1r">
                {bridge.cardHeadline}
              </h2>
              <p className="mt-4 text-gray-600 leading-relaxed" data-id="element-150" data-mp="22trv0dpubio9">
                {bridge.cardSubhead}
              </p>
              <button onClick={onCta} className="mt-8 w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2" style={{
              backgroundColor: university.ctaColor,
              color: university.ctaTextColor || '#ffffff'
            }} data-id="element-151" data-mp="1xwryatevrs9t">
                {bridge.ctaLabel}
              </button>
              <p className="mt-4 text-xs text-gray-400" data-id="element-152" data-mp="2p4a1f14kw50h">
                {university.accreditation}
              </p>
            </div>
          </div>
        </section>

        {/* Stats strip */}
        {landing?.stats && landing.stats.length > 0 && <section className="bg-white border-b border-gray-100 py-10 md:py-14 px-4" data-id="element-153" data-mp="2y69fplo7kuzq">
            <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-154" data-mp="334ktg7hmjpn8">
              {landing.stats.map(stat => <div key={stat.label} className="text-center" data-id="element-155" data-mp="3c3fyvhcugvph">
                  <div className="text-3xl md:text-4xl font-extrabold mb-1" style={{
              color: university.primaryColor
            }} data-id="element-156" data-mp="3c1v1zl3250og">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm font-semibold uppercase tracking-wider text-gray-500" data-id="element-157" data-mp="3qtdrqalpy9l0">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </section>}

        {/* Short paragraph section */}
        <section className="bg-gray-50 py-16 md:py-20 px-4" data-id="element-158" data-mp="314yl1m6sufzy">
          <div className="max-w-3xl mx-auto text-center" data-id="element-159" data-mp="316jet9lahuwr">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
            color: university.ctaColor
          }} data-id="element-160" data-mp="3ftup785qj8va">
              Why {university.shortName}
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-8" data-id="element-161" data-mp="3ot9cjyqlv3bk">
              {bridge.sectionHeading}
            </h2>
            <div className="space-y-6 text-left md:text-center" data-id="element-162" data-mp="2n2qfo6uup8c">
              {bridge.paragraphs.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-163" data-mp="3tsuhqqwdc7wq">
                  {paragraph}
                </p>)}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        {landing?.features && landing.features.length > 0 && <section className="bg-white py-16 md:py-20 px-4" data-id="element-164" data-mp="jwf3cbk3zk9z">
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-165" data-mp="jvvm6x2a8zx0">
              {landing.features.map(feature => {
            const Icon = featureIconMap[feature.icon] || GraduationCapIcon;
            return <div key={feature.title} className="bg-white rounded-xl border border-gray-200 p-6 text-center hover:shadow-md transition-shadow" data-id="element-166" data-mp="3sbp1j8kvo5ch">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{
                backgroundColor: `${university.primaryColor}14`
              }} data-id="element-167" data-mp="3ieu6izntcn2y">
                      <Icon className="w-7 h-7" style={{
                  color: university.primaryColor
                }} data-id="element-168" data-mp="1gnh0u8e0ub5u" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-169" data-mp="8ee80wg60nfv">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-170" data-mp="4foosn8g4bob">
                      {feature.description}
                    </p>
                  </div>;
          })}
            </div>
          </section>}

        {/* Closing CTA band */}
        <section className="py-16 md:py-20 px-4" style={{
        backgroundColor: university.primaryColor
      }} data-id="element-171" data-mp="3lv3tls6l3r3x">
          <div className="max-w-4xl mx-auto text-center text-white" data-id="element-172" data-mp="3ct45yh12dos0">
            <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4" data-id="element-173" data-mp="3cv8b12ap8ttv">
              {landing?.finalCta?.headline || bridge.cardHeadline}
            </h2>
            {landing?.finalCta?.body && <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-174" data-mp="1azes24bbggv1">
                {landing.finalCta.body}
              </p>}
            <button onClick={onCta} className="px-8 py-4 font-bold rounded-lg text-base md:text-lg transition-all shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white" style={{
            backgroundColor: university.ctaColor,
            color: university.ctaTextColor || '#ffffff'
          }} data-id="element-175" data-mp="1dlnrhbjuqsnp">
              {landing?.finalCta?.buttonLabel || bridge.ctaLabel}
            </button>
          </div>
        </section>

        <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs" data-id="element-176" data-mp="zo8yjwue42qz">
          <div className="max-w-5xl mx-auto space-y-2" data-id="element-177" data-mp="14nlnqo8qhlwq">
            <p data-id="element-178" data-mp="18o5hriy0g9en">
              © {new Date().getFullYear()} {university.name}. All rights
              reserved.
            </p>
            <p className="text-gray-500" data-id="element-179" data-mp="2g0ebzp2bfs81">
              {university.accreditation} • Founded {university.founded}
            </p>
            {bridge.disclosure && <p className="text-gray-500 max-w-2xl mx-auto pt-2" data-id="element-180" data-mp="34xtgu3v93wbv">
                {bridge.disclosure}
              </p>}
            <div className="max-w-2xl mx-auto pt-2" data-id="element-181" data-mp="39w4smk53wgwq">
              <TrackingNoticeLinks data-id="element-182" data-mp="3eoebjg7t0gpi" />
            </div>
          </div>
        </footer>
      </main>
    </div>;
}